/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from "react";
import { Loading, useTranslate } from "react-admin";
import AccessDenied from "layout/AccessDenied";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";
import FolderOpenIcon from "@material-ui/icons/FolderOpen";
import FolderIcon from "@material-ui/icons/Folder";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import { getAccountGroupingTree } from "services/setting";
import DetailPanel from "./Dialog/AccountGrouping";
import DialogMoveGroup from "./Dialog/AccountGroupingMove";

const useStyles = makeStyles((theme) => ({
  tabPanel: { width: "100%", margin: "0 20px" },
  container: {
    display: "flex",
    gap: theme.spacing(2),
    minHeight: "calc(100vh - 220px)",
  },
  treePanel: {
    width: "34%",
    minWidth: 340,
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    overflow: "hidden",
    backgroundColor: "#fbfcff",
  },
  treeHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: "#fff",
  },
  treeTitleWrap: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  treeTitle: {
    fontWeight: 700,
    color: "#1f2a44",
  },
  treeSubtitle: {
    fontSize: "0.74rem",
    fontWeight: 700,
    color: "#8b97ad",
    textTransform: "uppercase",
  },
  addButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: "linear-gradient(135deg, #5b50f6 0%, #4b67ff 100%)",
    color: "#fff",
    "&:hover": {
      background: "linear-gradient(135deg, #4d43ea 0%, #3f5cf4 100%)",
    },
  },
  searchBox: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: "#fff",
  },
  searchInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: "#f4f6fb",
    },
  },
  treeBody: {
    overflowY: "auto",
    padding: theme.spacing(1.25),
    flex: 1,
  },
  nodeRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.1, 1.25),
    borderRadius: 12,
    cursor: "pointer",
    border: "1px solid transparent",
    marginBottom: theme.spacing(0.75),
    backgroundColor: "#fff",
    "&:hover": {
      borderColor: "#d4ddff",
      boxShadow: "0 6px 20px rgba(65, 88, 208, 0.08)",
    },
  },
  nodeSelected: {
    borderColor: "#aebcff",
    backgroundColor: "#eef1ff",
    boxShadow: "0 10px 24px rgba(65, 88, 208, 0.12)",
  },
  nodeIndent: {
    width: 14,
    flexShrink: 0,
  },
  nodeTextWrap: {
    minWidth: 0,
    flex: 1,
  },
  nodeCode: {
    fontSize: "0.72rem",
    fontWeight: 700,
    color: "#7a86a0",
    marginBottom: 2,
  },
  nodeNameRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  nodeName: {
    color: "#1f2a44",
    fontWeight: 700,
    fontSize: "0.92rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  levelChip: {
    borderRadius: 10,
    fontSize: "0.7rem",
    fontWeight: 700,
    backgroundColor: "#f3f5fb",
    color: "#52607a",
  },
  accountLeaf: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    padding: theme.spacing(0.6, 1.25, 0.6, 1.25),
    color: "#7c879a",
  },
  accountLeafText: {
    fontSize: "0.82rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  detailPanel: {
    flex: 1,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    overflow: "hidden",
    backgroundColor: "#fff",
  },
  emptyState: {
    color: "#95a0b5",
    textAlign: "center",
    marginTop: theme.spacing(5),
    padding: theme.spacing(0, 2),
  },
}));

const filterTree = (nodes, keyword) => {
  if (!keyword) return nodes;
  const normalized = keyword.trim().toLowerCase();
  if (!normalized) return nodes;

  return (nodes || []).reduce((result, node) => {
    const children = filterTree(node.Children || [], keyword);
    const matchesNode = [node.GroupCode, node.GroupName]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(normalized));

    if (matchesNode || children.length > 0) {
      result.push({
        ...node,
        Children: children,
      });
    }
    return result;
  }, []);
};

const TreeNode = ({
  node,
  level,
  selectedId,
  onSelect,
  expandedMap,
  setExpandedMap,
}) => {
  const classes = useStyles();
  const hasChildren = (node.Children || []).length > 0;
  const hasAccounts = (node.Accounts || []).length > 0;
  const expanded = expandedMap[node.Id] ?? true;
  const isSelected = Number(selectedId) === Number(node.Id);

  return (
    <div>
      <div
        className={`${classes.nodeRow} ${isSelected ? classes.nodeSelected : ""}`}
        onClick={() => onSelect(node)}
      >
        <div className={classes.nodeIndent} style={{ marginLeft: level * 16 }} />
        {(hasChildren || hasAccounts) ? (
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setExpandedMap((prev) => ({
                ...prev,
                [node.Id]: !expanded,
              }));
            }}
          >
            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        ) : (
          <div style={{ width: 32 }} />
        )}
        {expanded ? (
          <FolderOpenIcon fontSize="small" style={{ color: "#6a76f6" }} />
        ) : (
          <FolderIcon fontSize="small" style={{ color: "#6a76f6" }} />
        )}
        <div className={classes.nodeTextWrap}>
          <div className={classes.nodeCode}>{node.GroupCode}</div>
          <div className={classes.nodeNameRow}>
            <div className={classes.nodeName}>{node.GroupName}</div>
            <Chip label={`L${node.Level}`} size="small" className={classes.levelChip} />
          </div>
        </div>
      </div>

      {expanded && hasChildren
        ? node.Children.map((child) => (
            <TreeNode
              key={child.Id}
              node={child}
              level={level + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              expandedMap={expandedMap}
              setExpandedMap={setExpandedMap}
            />
          ))
        : null}

      {expanded && hasAccounts
        ? node.Accounts.map((account) => (
            <div
              key={account.Id}
              className={classes.accountLeaf}
              style={{ paddingLeft: 56 + (level + 1) * 16 }}
            >
              <InsertDriveFileIcon fontSize="small" style={{ color: "#b0b7c7" }} />
              <div className={classes.accountLeafText}>
                {account.AccCode} - {account.Description}
              </div>
            </div>
          ))
        : null}
    </div>
  );
};

const AccountGrouping = (props) => {
  const translate = useTranslate();
  const { value, index } = props;
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [treeData, setTreeData] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [mode, setMode] = useState("view");
  const [parentNode, setParentNode] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [expandedMap, setExpandedMap] = useState({});
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);

  // mock permission until BE support
  let checkPermission = true;
  // let checkPermission = permissions?.find((i) => i.Name === "GL.AccountGrouping")?.View;

  const fetchTreeData = useCallback(async (preferredId, nextMode = "view") => {
    setLoading(true);
    const { Data } = await getAccountGroupingTree();
    const nextTree = Data || [];
    setTreeData(nextTree);

    if (preferredId === 0) {
      setSelectedNode(null);
      setMode("add");
    } else if (preferredId) {
      const findSelected = (nodes) => {
        for (const node of nodes || []) {
          if (Number(node.Id) === Number(preferredId)) return node;
          const nested = findSelected(node.Children);
          if (nested) return nested;
        }
        return null;
      };

      const found = findSelected(nextTree);
      setSelectedNode(found || nextTree[0] || null);
      setMode(nextMode);
    } else {
      setSelectedNode((current) => {
        if (!current) return nextTree[0] || null;
        const findCurrent = (nodes) => {
          for (const node of nodes || []) {
            if (Number(node.Id) === Number(current.Id)) return node;
            const nested = findCurrent(node.Children);
            if (nested) return nested;
          }
          return null;
        };
        return findCurrent(nextTree) || nextTree[0] || null;
      });
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    if (value === index && checkPermission) {
      fetchTreeData();
    }
  }, [value, index, checkPermission, fetchTreeData]);

  const filteredTree = filterTree(treeData, searchText);

  const handleSelectNode = (node) => {
    setSelectedNode(node);
    setParentNode(null);
    setMode("view");
  };

  const handleAddRoot = () => {
    setSelectedNode(null);
    setParentNode(null);
    setMode("add");
  };

  const handleAddChild = (node) => {
    setSelectedNode(null);
    setParentNode(node);
    setMode("add");
  };

  const handleRequestMoveGroup = () => {
    if (!selectedNode) return;
    setMoveDialogOpen(true);
  };

  const handleDetailClose = (result) => {
    if (typeof result === "number") {
      setParentNode(null);
      fetchTreeData(result, "view");
      return;
    }

    if (parentNode) {
      setSelectedNode(parentNode);
      setParentNode(null);
      setMode("view");
      return;
    }

    if (selectedNode) {
      setParentNode(null);
      setMode("view");
      return;
    }

    setParentNode(null);
    setSelectedNode(treeData[0] || null);
    setMode("view");
  };

  const handleCloseMoveDialog = (result) => {
    setMoveDialogOpen(false);
    if (result && selectedNode?.Id) {
      fetchTreeData(selectedNode.Id, "view");
    }
  };

  if (loading && treeData.length === 0) {
    return (
      <div style={{ marginInline: "auto" }}>
        <Loading />
      </div>
    );
  }

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      className={classes.tabPanel}
    >
      {checkPermission && value === index ? (
        <div className={classes.container}>
          <Paper className={classes.treePanel} elevation={0}>
            <div className={classes.treeHeader}>
              <div className={classes.treeTitleWrap}>
                <Typography variant="h6" className={classes.treeTitle}>
                  {translate("ra.module.Account Grouping")}
                </Typography>
                <div className={classes.treeSubtitle}>4 levels hierarchy</div>
              </div>
              <IconButton className={classes.addButton} onClick={handleAddRoot}>
                <AddIcon fontSize="small" />
              </IconButton>
            </div>

            <div className={classes.searchBox}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder={translate("ra.field.Search groups...")}
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                className={classes.searchInput}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <div className={classes.treeBody}>
              {!filteredTree.length ? (
                <Typography variant="body2" className={classes.emptyState}>
                  {translate("ra.info.notransaction")}
                </Typography>
              ) : (
                filteredTree.map((node) => (
                  <TreeNode
                    key={node.Id}
                    node={node}
                    level={0}
                    selectedId={selectedNode?.Id}
                    onSelect={handleSelectNode}
                    expandedMap={expandedMap}
                    setExpandedMap={setExpandedMap}
                  />
                ))
              )}
            </div>
          </Paper>

          <Paper className={classes.detailPanel} elevation={0}>
            <DetailPanel
              key={`${mode}-${selectedNode?.Id || 0}-${parentNode?.Id || 0}`}
              id={mode === "add" ? 0 : selectedNode?.Id || 0}
              title={translate("ra.module.Account Grouping")}
              mode={mode}
              setMode={setMode}
              open
              onClose={handleDetailClose}
              parentNode={parentNode}
              currentNode={selectedNode}
              treeData={treeData}
              embedded
              onAddChild={handleAddChild}
              onRequestMoveGroup={handleRequestMoveGroup}
            />
          </Paper>

          {moveDialogOpen && selectedNode ? (
            <DialogMoveGroup
              id={selectedNode.Id}
              title={translate("ra.field.Move")}
              open={moveDialogOpen}
              onClose={handleCloseMoveDialog}
              currentNode={selectedNode}
              treeData={treeData}
            />
          ) : null}
        </div>
      ) : (
        <AccessDenied />
      )}
    </div>
  );
};

export default AccountGrouping;
